<script setup lang="ts">
import Dialog from 'primevue/dialog'
import { computed, onMounted, onUnmounted, ref, shallowRef } from 'vue'

type TextField = HTMLInputElement | HTMLTextAreaElement
type KeyboardRow = { characters: string; indented?: boolean; shifted?: boolean }
type KeyboardKey = 'shift' | { character: string; indented: boolean }

const textInputTypes = ['text', 'email', 'password', 'number', 'search', 'tel', 'url']
const keypadCharacters = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
const letterRows: KeyboardRow[] = [
  { characters: '1234567890' },
  { characters: 'qwertyuiop' },
  { characters: 'asdfghjkl', indented: true },
  { characters: 'zxcvbnm', shifted: true }
]
const symbolRows: KeyboardRow[] = [
  { characters: '1234567890' },
  { characters: '!#$%^&*()-' },
  { characters: '_=+[]{}\\|~' },
  { characters: '`;:\'",<>/?' }
]

const field = shallowRef<TextField>()
const text = ref('')
const symbols = ref(false)
const shift = ref(false)
const revealed = ref(false)
let pressUsedByKeyboard = false
let pressStartedOffKeyboard = false

const visible = computed({
  get: () => field.value !== undefined,
  set: open => {
    if (!open) {
      close()
    }
  }
})
const keypad = computed(() => field.value?.type === 'number' || field.value?.inputMode === 'numeric')
const masked = computed(() => field.value?.type === 'password')
const hiddenFromRecordings = computed(() => masked.value || field.value?.closest('[data-private]') != null)
const title = computed(() => field.value?.labels?.[0]?.textContent?.trim() || field.value?.placeholder)
const shownText = computed(() => (masked.value && !revealed.value ? '•'.repeat(text.value.length) : text.value))
const keyboardKeys = computed(() => (symbols.value ? symbolRows : letterRows).flatMap(keysOf))

function keysOf(row: KeyboardRow): KeyboardKey[] {
  const characters = shift.value && !symbols.value ? row.characters.toUpperCase() : row.characters
  const keys = [...characters].map((x, index) => ({ character: x, indented: row.indented === true && index === 0 }))
  return row.shifted ? ['shift', ...keys, 'shift'] : keys
}

function isTextField(target: EventTarget | null): target is TextField {
  const editable =
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLInputElement && textInputTypes.includes(target.type))
  return editable && !target.disabled && !target.readOnly
}

function onPress(event: PointerEvent) {
  pressStartedOffKeyboard = !visible.value
  if (pressStartedOffKeyboard) {
    openForTappedField(event)
  }
  pressUsedByKeyboard = visible.value
}

function keepPressOffKeys(event: PointerEvent) {
  if (pressStartedOffKeyboard) {
    event.stopPropagation()
  }
}

function keepClickOnKeyboard(event: MouseEvent) {
  if (pressUsedByKeyboard) {
    event.stopPropagation()
  }
}

function openForTappedField(event: PointerEvent) {
  if (!isTextField(event.target)) {
    return
  }
  event.preventDefault()
  field.value = event.target
  text.value = event.target.value
  symbols.value = false
  shift.value = false
  revealed.value = false
  event.target.dispatchEvent(new FocusEvent('focus'))
}

function enter(next: string) {
  const maxLength = field.value!.maxLength
  if (maxLength > 0 && next.length > maxLength) {
    return
  }
  text.value = next
  field.value!.value = next
  field.value!.dispatchEvent(new Event('input', { bubbles: true }))
}

function press(character: string) {
  enter(text.value + character)
  shift.value = false
}

function close() {
  field.value!.dispatchEvent(new Event('change', { bubbles: true }))
  field.value!.dispatchEvent(new FocusEvent('blur'))
  field.value = undefined
}

onMounted(() => {
  document.addEventListener('pointerdown', onPress, true)
  window.addEventListener('click', keepClickOnKeyboard, true)
})
onUnmounted(() => {
  document.removeEventListener('pointerdown', onPress, true)
  window.removeEventListener('click', keepClickOnKeyboard, true)
})
</script>

<template>
  <Dialog
    v-model:visible="visible"
    data-testid="on-screen-keyboard"
    :data-private="hiddenFromRecordings || undefined"
    modal
    dismissableMask
    :draggable="false"
    :closeButtonProps="{ severity: 'secondary', text: true, rounded: true, onPointerup: close }"
    :header="title"
    :class="keypad ? undefined : 'w-4/5'"
    @pointerup.capture="keepPressOffKeys"
  >
    <div class="flex flex-col gap-4">
      <div class="flex gap-2">
        <div
          class="flex h-16 grow items-center justify-end overflow-hidden rounded-xl border-2 border-surface-300 px-4"
        >
          <span class="shrink-0 whitespace-pre text-3xl font-semibold" v-text="shownText" />
          <span class="h-9 w-0.5 shrink-0 animate-pulse bg-primary" />
        </div>
        <Button
          v-if="masked"
          variant="outline"
          size="sm"
          class="h-16"
          :icon="revealed ? 'eyeOff' : 'eye'"
          :aria-label="revealed ? 'Hide' : 'Show'"
          @pointerup="revealed = !revealed"
        />
      </div>
      <div v-if="keypad" class="grid grid-cols-3 gap-2">
        <Button v-for="x in keypadCharacters" :key="x" variant="outline" size="lg" @pointerup="press(x)">
          {{ x }}
        </Button>
        <Button
          variant="outline"
          size="lg"
          icon="backspace"
          aria-label="Backspace"
          @pointerup="enter(text.slice(0, -1))"
        />
        <Button variant="outline" size="lg" @pointerup="press('0')">0</Button>
        <Button data-testid="on-screen-keyboard-done" size="lg" @pointerup="close">Done</Button>
      </div>
      <div v-else class="grid grid-cols-20 gap-2">
        <template v-for="(x, index) in keyboardKeys" :key="index">
          <Button
            v-if="x === 'shift'"
            data-testid="on-screen-keyboard-shift"
            :variant="shift ? undefined : 'outline'"
            size="sm"
            class="col-span-3 h-16"
            icon="shift"
            aria-label="Shift"
            @pointerup="shift = !shift"
          />
          <Button
            v-else
            variant="outline"
            size="lg"
            :class="['col-span-2 h-16', x.indented && 'col-start-2']"
            @pointerup="press(x.character)"
          >
            {{ x.character }}
          </Button>
        </template>
        <Button
          data-testid="on-screen-keyboard-symbols"
          variant="outline"
          size="lg"
          class="col-span-3 h-16"
          @pointerup="symbols = !symbols"
        >
          {{ symbols ? 'ABC' : '#+=' }}
        </Button>
        <Button variant="outline" size="lg" class="col-span-2 h-16" @pointerup="press('@')">@</Button>
        <Button variant="outline" size="lg" class="col-span-6 h-16" aria-label="Space" @pointerup="press(' ')" />
        <Button variant="outline" size="lg" class="col-span-2 h-16" @pointerup="press('.')">.</Button>
        <Button
          variant="outline"
          size="sm"
          class="col-span-3 h-16"
          icon="backspace"
          aria-label="Backspace"
          @pointerup="enter(text.slice(0, -1))"
        />
        <Button data-testid="on-screen-keyboard-done" size="lg" class="col-span-4 h-16" @pointerup="close">Done</Button>
      </div>
    </div>
  </Dialog>
</template>
